import withParams from "@src/components/widgets/HOC/withParams";
import React, { useCallback } from "react";
import moment from "moment";
import { WidgetTemplateComponentType } from "@src/interface/Widget";
import { ILandingStandardWidgetVideoSliderAdrienCachotParams } from "../interface";
import LandingVideoSliderAdrienCachot, {
  IVideoSliderItem,
} from "design-system/src/Components/WidgetPart/LandingVideoSliderAdrienCachot/LandingVideoSliderAdrienCachot";
import { useTranslation } from "@src/hooks/useTranslation";
import SushishopVideo from "@src/lib/render-video/SushishopVideo/SushishopVideo";
import { GATSBY_SOURCE_SUSHISHOP_BASE_URL } from "@src/utils/constants";
import { responsiveMobileBreakpoint } from "design-system/src/Components/Grid/_utils/responsiveObserve";

const VideoSliderAdrienCachot: WidgetTemplateComponentType =
  withParams<ILandingStandardWidgetVideoSliderAdrienCachotParams>((props) => {
    const { p } = props;
    const { t } = useTranslation("landing");

    /** Jour + mois long, selon la locale moment (IsoLangContext). */
    const formatAvailableDateDisplay = useCallback((iso: string) => {
      const m = moment(iso);
      return m.isValid() ? m.format("D MMMM") : "";
    }, []);

    const renderVideo = useCallback(
      (videoSlug: string, onEnded: () => void) => {
        const isMobile =
          typeof window !== "undefined" &&
          window.matchMedia(responsiveMobileBreakpoint).matches;
        return (
          <SushishopVideo
            slug={videoSlug}
            resize="full"
            autoPlay={true}
            controls={true}
            muted={false}
            playsInline={!isMobile}
            onEnded={onEnded}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            wrapperStyles={{ width: "100%", height: "100%" }}
          />
        );
      },
      []
    );

    const handleMobilePlay = useCallback((videoSlug: string | undefined) => {
      if (!videoSlug) return;
      const videoUrl = `${GATSBY_SOURCE_SUSHISHOP_BASE_URL}/USER/video/exports/${videoSlug}.480p.full.mp4`;
      const video = document.createElement("video");
      video.src = videoUrl;
      video.controls = true;
      video.style.cssText =
        "position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;";
      document.body.appendChild(video);

      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        video.pause();
        video.remove();
        video.removeEventListener("webkitendfullscreen", cleanup);
        document.removeEventListener("fullscreenchange", onFullscreenChange);
      };

      const onFullscreenChange = () => {
        if (!document.fullscreenElement) cleanup();
      };

      video.addEventListener("webkitendfullscreen", cleanup);
      document.addEventListener("fullscreenchange", onFullscreenChange);

      // iOS Safari : sans attribut playsInline, play() ouvre automatiquement le
      // player natif en plein écran. Doit être appelé synchrone pendant le geste.
      video.play().catch(cleanup);

      // Android / autres : requestFullscreen explicite (pas nécessaire sur iOS)
      const el = video as HTMLVideoElement & {
        webkitEnterFullscreen?: () => void;
      };
      if (!el.webkitEnterFullscreen && video.requestFullscreen) {
        video.requestFullscreen().catch(cleanup);
      }
    }, []);

    const videos: IVideoSliderItem[] = [
      {
        title: p("title_1"),
        subtitle: p("subtitle_1"),
        coverImage: p("cover_1"),
        videoSlug: p("video_src_1"),
        videoSlugMobile: p("video_src_mobile_1"),
        availableDate: p("available_date_1"),
      },
      {
        title: p("title_2"),
        subtitle: p("subtitle_2"),
        coverImage: p("cover_2"),
        videoSlug: p("video_src_2"),
        videoSlugMobile: p("video_src_mobile_2"),
        availableDate: p("available_date_2"),
      },
      {
        title: p("title_3"),
        subtitle: p("subtitle_3"),
        coverImage: p("cover_3"),
        videoSlug: p("video_src_3"),
        videoSlugMobile: p("video_src_mobile_3"),
        availableDate: p("available_date_3"),
      },
    ].filter((v) => v.title || v.coverImage);

    return (
      <LandingVideoSliderAdrienCachot
        title={p("section_title")}
        videos={videos}
        watchNowLabel={t("Regardez dès maintenant", "landing")}
        newEpisodeLabel={t("Nouvel épisode le", "landing")}
        formatAvailableDateDisplay={formatAvailableDateDisplay}
        renderVideo={renderVideo}
        onMobilePlay={handleMobilePlay}
      />
    );
  });

export default VideoSliderAdrienCachot;
